import React, { useEffect, useMemo, useState } from "react";
import { StyledInput } from "./styles";
import { StyledNumberInlineInput } from "../../common.styles";
import { toLocaleNumber, toRawNumber } from "../../../../../utils/translation";
import {
  handleNumberInputKeyDown,
  handleNumberInputPaste,
} from "../../../../../utils/common";

type Props = {
  value: string | number | undefined | boolean;
  onChange?: (value: string | number) => void;
  maxLimit?: number;
  minLimit?: number;
  disabled?: boolean;
  placeholder?: string;
  width?: number;
  backgroundColor?: string;
  inline?: boolean;
  id: string;
  lang: string;
};

export const TableNumberInput: React.FC<Props> = ({
  value = "",
  onChange,
  maxLimit = undefined,
  disabled = false,
  minLimit = 0,
  placeholder = "",
  width = 100,
  backgroundColor = "transparent",
  inline = false,
  id,
  lang = "english",
}) => {
  const [data, setData] = useState(value?.toString() || "");
  const ref = React.useRef<HTMLInputElement>(null);
  useEffect(() => {
    setData(value?.toString() || "");
  }, [value]);
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const rawNumber = toRawNumber(value, lang);
    if (value === "") {
      setData("");
      onChange?.("");
      return;
    }
    if (value || value === "0") {
      const parsedValue = parseFloat(rawNumber);
      const v =
        maxLimit && parsedValue > maxLimit
          ? maxLimit
          : minLimit > parsedValue
          ? "0"
          : rawNumber;
      if (maxLimit && parsedValue > maxLimit && ref.current) {
        ref.current.value = maxLimit.toString();
      }
      if (minLimit > parsedValue && ref.current) {
        ref.current.value = minLimit.toString();
      }
      const preciseVal: string = Number(v).toFixed(2);
      setData(v.toString());
      onChange?.(Number(preciseVal));
    }
  };

  const handleBlur = () => {
    if (data === "") return onChange?.("");
    const fixedValue: string = Number(data).toFixed(2);
    setData(Number(fixedValue).toString());
    onChange?.(fixedValue);
  };

  const displayData = useMemo(() => {
    const val = toLocaleNumber(data, lang);
    if (val === "NaN") return "";
    return val;
  }, []);

  if (inline) {
    return (
      <StyledNumberInlineInput
        id={id}
        ref={ref}
        defaultValue={displayData}
        onChange={handleChange}
        onBlur={handleBlur}
        disabled={disabled}
        placeholder={placeholder}
        onKeyDown={(e) => handleNumberInputKeyDown(e, lang)}
        onPaste={(e) => handleNumberInputPaste(e, lang)}
        type="text"
      />
    );
  }
  return (
    <StyledInput
      id={id}
      ref={ref}
      defaultValue={displayData}
      backgroundColor={backgroundColor}
      onChange={handleChange}
      onBlur={handleBlur}
      disabled={disabled}
      placeholder={placeholder}
      width={width}
      onKeyDown={(e) => handleNumberInputKeyDown(e, lang)}
      onPaste={(e) => handleNumberInputPaste(e, lang)}
      type="text"
    />
  );
};
